/**
 * settings-api — thin client for `/api/v1/business/settings`.
 *
 * The backend endpoint for tenant-scoped business settings is not yet
 * wired (see PETOTA_BUSINESS_UX_BLUEPRINT Faz 0.5 — BusinessProfileEditor
 * and the ADR-100 BusinessProfileRegistry work). Until then this module
 * returns a locally-persisted mock so the settings UI can be built and
 * validated end-to-end without blocking on backend delivery.
 *
 * When the real endpoint lands, replace the `mockFetch` / `mockUpdate`
 * bodies with `apiClient.get/put('/v1/business/settings')` — the shape
 * defined in `types.ts` is the contract.
 */
import type { BusinessSettingsPayload } from './types';

const STORAGE_KEY = 'petota-business:settings-mock';

const DEFAULT_SETTINGS: BusinessSettingsPayload = {
  profile: {
    legalName: '',
    displayName: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    addressLine: '',
    city: '',
    postalCode: '',
    timezone: 'Europe/Istanbul',
    currency: 'TRY',
    locale: 'tr',
  },
  hours: {
    monday: { closed: false, open: '09:00', close: '18:00' },
    tuesday: { closed: false, open: '09:00', close: '18:00' },
    wednesday: { closed: false, open: '09:00', close: '18:00' },
    thursday: { closed: false, open: '09:00', close: '18:00' },
    friday: { closed: false, open: '09:00', close: '18:00' },
    saturday: { closed: false, open: '10:00', close: '17:00' },
    sunday: { closed: true, open: '', close: '' },
  },
  notifications: {
    newBooking: { email: true, sms: false, push: true },
    cancellation: { email: true, sms: true, push: true },
    payment: { email: true, sms: false, push: true },
    lowStock: { email: false, sms: false, push: true },
    dailyDigest: { email: true, sms: false, push: false },
  },
  security: {
    twoFactorEnabled: false,
    sessions: [
      {
        id: 'sess-current',
        deviceLabel: 'iPad Pro — Safari',
        ipAddress: '10.0.0.12',
        lastActiveAt: new Date().toISOString(),
        isCurrent: true,
      },
    ],
  },
};

function readStore(): BusinessSettingsPayload {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return JSON.parse(raw) as BusinessSettingsPayload;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function writeStore(payload: BusinessSettingsPayload): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function fetchSettings(): Promise<BusinessSettingsPayload> {
  await delay(180);
  return readStore();
}

export async function updateSettings(
  patch: Partial<BusinessSettingsPayload>,
): Promise<BusinessSettingsPayload> {
  await delay(300);
  const current = readStore();
  const next: BusinessSettingsPayload = {
    profile: { ...current.profile, ...(patch.profile ?? {}) },
    hours: { ...current.hours, ...(patch.hours ?? {}) },
    notifications: { ...current.notifications, ...(patch.notifications ?? {}) },
    security: { ...current.security, ...(patch.security ?? {}) },
  };
  writeStore(next);
  return next;
}

export async function revokeSession(sessionId: string): Promise<void> {
  await delay(200);
  const current = readStore();
  writeStore({
    ...current,
    security: {
      ...current.security,
      sessions: current.security.sessions.filter((s) => s.id !== sessionId),
    },
  });
}

export async function revokeAllOtherSessions(): Promise<void> {
  await delay(300);
  const current = readStore();
  writeStore({
    ...current,
    security: {
      ...current.security,
      sessions: current.security.sessions.filter((s) => s.isCurrent),
    },
  });
}

export const settingsQueryKeys = {
  root: ['business-settings'] as const,
};
